import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { useAuth } from './useAuth'
import {
  isEntryBookmarked,
  addBookmark as addBookmarkFn,
  removeBookmark as removeBookmarkFn,
  getOrCreateBookmarkCollection
} from '@/lib/collections'

export function useBookmark(entryId: number) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const t = useTranslations('entry')

  // Query bookmark status
  const { data: isBookmarked = false, isLoading } = useQuery({
    queryKey: ['bookmark', entryId, user?.id],
    queryFn: () => {
      if (!user?.id) return false
      return isEntryBookmarked(user.id, entryId)
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false
  })

  // Add bookmark mutation
  const addMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not logged in')
      const bookmarkList = await getOrCreateBookmarkCollection(user.id)
      await addBookmarkFn(user.id, entryId)
      return bookmarkList.id
    },
    onMutate: async () => {
      // Optimistic update
      await queryClient.cancelQueries({
        queryKey: ['bookmark', entryId, user?.id]
      })
      await queryClient.cancelQueries({
        queryKey: ['all-collections', user?.id]
      })
      await queryClient.cancelQueries({ queryKey: ['bulk-bookmarks'] })

      const previousStatus = queryClient.getQueryData([
        'bookmark',
        entryId,
        user?.id
      ])

      // Update individual bookmark status
      queryClient.setQueryData(['bookmark', entryId, user?.id], true)

      // Optimistically update all bulk bookmark queries
      // Store previous states keyed by queryKey reference for rollback
      const previousBulkMaps = new Map<
        readonly unknown[],
        Map<number, boolean>
      >()
      const bulkQueries = queryClient.getQueriesData({
        queryKey: ['bulk-bookmarks'],
        exact: false
      })

      bulkQueries.forEach(([queryKey, data]) => {
        if (data instanceof Map) {
          const map = data as Map<number, boolean>
          // Store previous state for rollback (using queryKey reference as key)
          previousBulkMaps.set(queryKey, new Map(map))
          // Create new map with updated value (don't mutate existing)
          const updatedMap = new Map(map)
          updatedMap.set(entryId, true)
          queryClient.setQueryData(queryKey, updatedMap)
        }
      })

      return { previousStatus, previousBulkMaps }
    },
    onError: (err, variables, context) => {
      // Show error toast
      toast.error(t('bookmarkAddError'))

      // Rollback on error
      if (context?.previousStatus !== undefined) {
        queryClient.setQueryData(
          ['bookmark', entryId, user?.id],
          context.previousStatus
        )
      }

      // Rollback bulk bookmark maps
      if (context?.previousBulkMaps) {
        context.previousBulkMaps.forEach((previousMap, queryKey) => {
          queryClient.setQueryData(queryKey, previousMap)
        })
      }
    },
    onSettled: async (data) => {
      // No need to invalidate individual bookmark - already updated optimistically
      // It will refetch naturally when stale (5 min staleTime)
      queryClient.invalidateQueries({ queryKey: ['all-collections', user?.id] })

      // Invalidate collection entries for the bookmark collection
      if (data && user?.id) {
        queryClient.invalidateQueries({
          queryKey: ['collection-entries', data],
          exact: false
        })
      }

      // No need to invalidate bulk-bookmarks - we've already updated them optimistically
      // They'll be refetched naturally when they become stale (5 min staleTime)
    }
  })

  // Remove bookmark mutation
  const removeMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not logged in')
      const bookmarkList = await getOrCreateBookmarkCollection(user.id)
      await removeBookmarkFn(user.id, entryId)
      return bookmarkList.id
    },
    onMutate: async () => {
      // Optimistic update
      await queryClient.cancelQueries({
        queryKey: ['bookmark', entryId, user?.id]
      })
      await queryClient.cancelQueries({
        queryKey: ['all-collections', user?.id]
      })
      await queryClient.cancelQueries({ queryKey: ['bulk-bookmarks'] })

      const previousStatus = queryClient.getQueryData([
        'bookmark',
        entryId,
        user?.id
      ])

      // Update individual bookmark status
      queryClient.setQueryData(['bookmark', entryId, user?.id], false)

      // Optimistically update all bulk bookmark queries
      // Store previous states keyed by queryKey reference for rollback
      const previousBulkMaps = new Map<
        readonly unknown[],
        Map<number, boolean>
      >()
      const bulkQueries = queryClient.getQueriesData({
        queryKey: ['bulk-bookmarks'],
        exact: false
      })

      bulkQueries.forEach(([queryKey, data]) => {
        if (data instanceof Map) {
          const map = data as Map<number, boolean>
          // Store previous state for rollback (using queryKey reference as key)
          previousBulkMaps.set(queryKey, new Map(map))
          // Create new map with updated value (don't mutate existing)
          const updatedMap = new Map(map)
          updatedMap.set(entryId, false)
          queryClient.setQueryData(queryKey, updatedMap)
        }
      })

      return { previousStatus, previousBulkMaps }
    },
    onError: (err, variables, context) => {
      // Show error toast
      toast.error(t('bookmarkRemoveError'))

      // Rollback on error
      if (context?.previousStatus !== undefined) {
        queryClient.setQueryData(
          ['bookmark', entryId, user?.id],
          context.previousStatus
        )
      }

      // Rollback bulk bookmark maps
      if (context?.previousBulkMaps) {
        context.previousBulkMaps.forEach((previousMap, queryKey) => {
          queryClient.setQueryData(queryKey, previousMap)
        })
      }
    },
    onSettled: async (data) => {
      // No need to invalidate individual bookmark - already updated optimistically
      // It will refetch naturally when stale (5 min staleTime)
      queryClient.invalidateQueries({ queryKey: ['all-collections', user?.id] })

      // Invalidate collection entries for the bookmark collection
      if (data && user?.id) {
        queryClient.invalidateQueries({
          queryKey: ['collection-entries', data],
          exact: false
        })
      }

      // No need to invalidate bulk-bookmarks - we've already updated them optimistically
      // They'll be refetched naturally when they become stale (5 min staleTime)
    }
  })

  const toggleBookmark = async () => {
    if (isBookmarked) {
      await removeMutation.mutateAsync()
    } else {
      await addMutation.mutateAsync()
    }
  }

  return {
    isBookmarked,
    isLoading: isLoading || addMutation.isPending || removeMutation.isPending,
    toggleBookmark,
    addBookmark: () => addMutation.mutateAsync(),
    removeBookmark: () => removeMutation.mutateAsync()
  }
}
